import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollReveal from '@/components/ScrollReveal';
import {
  ArrowRight,
  CheckCircle,
  Network,
  Eye,
  Settings,
  Target,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const architectureLayers = [
  {
    title: 'Northbound Integration',
    items: ['ERP Systems', 'SAP', 'Oracle', 'Dynamics 365'],
    color: 'from-primary/30',
  },
  {
    title: 'Cognitive Core',
    items: ['Agentic AI', 'SLMs', 'Vision Models', 'Decision Engine'],
    color: 'from-primary/40',
  },
  {
    title: 'Edge Compute Layer',
    items: ['NVIDIA IGX', 'Jetson', 'Ruggedised Servers'],
    color: 'from-primary/30',
  },
  {
    title: 'Southbound Abstraction',
    items: ['OPC UA', 'Modbus', 'MQTT', 'PROFINET', 'EtherNet/IP'],
    color: 'from-primary/20',
  },
  {
    title: 'Factory Floor',
    items: ['PLCs', 'Sensors', 'Actuators', 'Vision Systems'],
    color: 'from-primary/10',
  },
];

const protocols = [
  {
    name: 'OPC UA',
    type: 'Unified Architecture',
    description:
      'The modern standard for interoperability. Provides semantic richness, allowing agents to "browse" and self-discover machine capabilities via metadata and information modeling.',
    pattern: 'Auto-discovery of node trees and data subscriptions',
  },
  {
    name: 'Modbus',
    type: 'RTU and TCP/IP',
    description:
      'The foundational standard for industrial communication. Because it lacks semantic context (transmitting only raw 16-bit registers), the AI requires a mapping layer to interpret data.',
    pattern: 'Cyclic polling based on static configuration maps',
  },
  {
    name: 'MQTT & Sparkplug B',
    type: 'High-Frequency Telemetry',
    description:
      'Optimised for high-frequency telemetry. Sparkplug B provides structured payloads and "Birth/Death Certificates," allowing the AI to maintain a real-time model of the device fleet.',
    pattern: 'Event-driven triggers for inference or logging',
  },
  {
    name: 'Real-Time Ethernet',
    type: 'PROFINET & EtherNet/IP',
    description:
      'Essential for motion control and safety. Integrated via gateways that expose cyclic data to the AI layer for real-time decision making.',
    pattern: 'Direct reading/writing of CIP tags by name',
  },
];

const hardwareTiers = [
  {
    badge: 'Tier 1',
    title: 'High-Performance Embedded',
    hardware: 'NVIDIA IGX / Jetson AGX Orin',
    description:
      'Used for autonomous machines and cobots. Bridges the gap between AI (Linux) and Safety (RTOS).',
  },
  {
    badge: 'Tier 2',
    title: 'Ruggedised Edge Server',
    hardware: 'Dell PowerEdge XR8000 / HPE Edgeline',
    description:
      'Hosts the central Cognitive Core for a production cell. Capable of running quantised 70B parameter models.',
  },
  {
    badge: 'Tier 3',
    title: 'Micro-Edge',
    hardware: 'NVIDIA Jetson Orin Nano',
    description:
      'Dedicated to single-task agents like visual inspection or quality control.',
  },
];

const agentRoles = [
  {
    icon: Target,
    title: 'Supervisor Agent',
    description:
      'Decomposes high-level objectives into sub-tasks and routes them using state machines. Orchestrates the overall workflow.',
  },
  {
    icon: Network,
    title: 'Protocol Agent',
    description:
      'Translates natural language requests into specific industrial protocol commands (e.g., OPC UA NodeIDs, Modbus registers).',
  },
  {
    icon: Eye,
    title: 'Diagnostic Agent',
    description:
      'Detects anomalies by monitoring telemetry streams against statistical baselines. Predicts failures before they occur.',
  },
  {
    icon: Settings,
    title: 'Control Agent',
    description:
      'The only agent authorised to write commands to PLCs. Operates within strict, hard-coded safety guardrails.',
  },
];

const roiDrivers = [
  {
    metric: '10-20%',
    title: 'OEE Improvement',
    description:
      'Reduced Unplanned Downtime through AI-driven predictive maintenance and anomaly detection.',
  },
  {
    metric: '↓ Scrap',
    title: 'Quality Yield',
    description:
      'Visual agents reduce scrap rates by catching defects at the source before they propagate.',
  },
  {
    metric: 'Faster',
    title: 'Operational Agility',
    description:
      'Democratised data access allows for faster, data-driven executive decision-making.',
  },
];

const rolloutPhases = [
  {
    label: 'Phase 1',
    duration: 'Months 1-3',
    title: '"Silent Observer"',
    description:
      'Read-only mode to validate AI accuracy and build trust. The system monitors, learns, and generates recommendations without taking action.',
    outcome: 'Baseline metrics established, AI accuracy validated',
  },
  {
    label: 'Phase 2',
    duration: 'Months 4-6',
    title: '"Co-Pilot"',
    description:
      'AI suggests changes that require human approval on the HMI. Operators validate recommendations before execution.',
    outcome: 'Trust established, operators trained, value demonstrated',
  },
  {
    label: 'Phase 3',
    duration: 'Month 7+',
    title: '"Bounded Autonomy"',
    description:
      'AI manages low-risk optimisation loops and automatic quality-based line stops within strict safety guardrails.',
    outcome: 'Full autonomous operation within defined boundaries',
  },
];

const differentiators = [
  {
    title: 'Deep Industrial Protocol Expertise',
    description:
      'Our team has hands-on experience with OPC UA, Modbus, PROFINET, and EtherNet/IP in production environments. We understand the nuances of real-time industrial communication.',
  },
  {
    title: 'Air-Gapped Security Architecture',
    description:
      'We design AI systems that operate within strict security constraints. Our "Fortress Factory" approach ensures AI reasoning occurs locally, isolated from external threats.',
  },
  {
    title: 'Edge-Native AI Deployment',
    description:
      'Experience deploying quantised SLMs and VLMs on NVIDIA edge hardware. We understand the constraints of industrial-grade compute environments.',
  },
  {
    title: 'Safety-First AI Design',
    description:
      'We architect AI systems that respect functional safety boundaries. Our systems are designed to enhance operations without compromising safety integrity levels.',
  },
  {
    title: 'ERP Integration Experience',
    description:
      'Proven integration patterns for SAP, Oracle, Dynamics 365, and manufacturing-specific ERPs like Epicor. We bridge the IT/OT divide.',
  },
  {
    title: 'Production-Proven Methodology',
    description:
      'Our phased rollout approach—Silent Observer → Co-Pilot → Bounded Autonomy—builds trust and demonstrates value before granting AI control.',
  },
];

export default function ManufacturingPage() {
  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="pt-44 pb-8 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/industries/manufacturing.jpg"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/90 to-white" />
          </div>
          <div className="container-custom relative z-10">
            <ScrollReveal className="text-center max-w-4xl mx-auto">
              <span className="text-primary text-sm font-medium uppercase tracking-wider">
                Industry 4.0
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mt-4 mb-6">
                Cognitive Industrial Edge
              </h1>
              <p className="text-lg sm:text-xl text-slate-600">
                Agentic AI Architecture for Manufacturing Intelligence
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Executive Summary */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                  The Future of Factory Intelligence
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  The industrial sector is transitioning from deterministic
                  Operational Technology (OT) to a "Cognitive Industrial Edge,"
                  where probabilistic Generative AI is integrated directly into
                  factory operations.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="text-slate-900 font-semibold mb-2">
                      Beyond Industry 4.0
                    </h3>
                    <p className="text-slate-600 text-sm">
                      Address the limitations of siloed data and fragmented legacy
                      protocols by deploying autonomous AI agents within secure,
                      air-gapped environments.
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="text-slate-900 font-semibold mb-2">
                      Edge-Native AI
                    </h3>
                    <p className="text-slate-600 text-sm">
                      Small Language Models (SLMs) and quantised LLMs on robust
                      edge compute enable machinery to reason about its state and
                      autonomously optimise production.
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h3 className="text-slate-900 font-semibold mb-2">
                      Secure by Design
                    </h3>
                    <p className="text-slate-600 text-sm">
                      Air-gapped "Fortress Factory" architecture ensures AI
                      reasoning occurs locally, isolated from public internet
                      threats.
                    </p>
                  </div>
                </div>

                <p className="text-slate-900 font-medium">
                  The result: A resilient, secure ecosystem that transforms
                  manufacturing from passive data collection to active, agentic
                  orchestration — delivering significant improvements in Overall
                  Equipment Effectiveness (OEE) and quality yield.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Architecture Overview */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Architecture Overview
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                The Cognitive Industrial Edge bridges the gap between low-level
                industrial protocols ("Southbound") and high-level Enterprise
                Resource Planning systems ("Northbound").
              </p>
            </ScrollReveal>

            <div className="max-w-3xl mx-auto space-y-4">
              {architectureLayers.map((layer, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div
                    className={`relative bg-gradient-to-r ${layer.color} to-transparent rounded-xl p-6 border border-slate-200`}
                  >
                    <h3 className="text-slate-900 font-semibold mb-2">
                      {layer.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {layer.items.join(' • ')}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Industrial Connectivity */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Industrial Connectivity & Protocol Abstraction
              </h2>
              <p className="text-slate-600 max-w-3xl mx-auto">
                The "Southbound" layer manages the heterogeneous communication
                environment of the factory floor, normalising diverse data dialects
                into a semantic model usable by AI agents.
              </p>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {protocols.map((protocol, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card h-full">
                    <div className="flex items-center gap-3 mb-3">
                      <Network className="w-6 h-6 text-primary" />
                      <div>
                        <h3 className="text-slate-900 font-semibold">
                          {protocol.name}
                        </h3>
                        <p className="text-primary text-sm">{protocol.type}</p>
                      </div>
                    </div>
                    <p className="text-slate-600 text-sm mb-4">
                      {protocol.description}
                    </p>
                    <div className="bg-primary/10 rounded-lg p-3">
                      <p className="text-primary text-xs font-medium">
                        AI Pattern: {protocol.pattern}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Edge Compute & Security */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Edge Compute & Air-Gapped Security
              </h2>
              <p className="text-slate-600 max-w-3xl mx-auto">
                Security requirements for critical infrastructure necessitate a
                "Fortress Factory" model where AI reasoning occurs locally, isolated
                from the public internet.
              </p>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {hardwareTiers.map((tier, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card h-full">
                    <span className="inline-block px-3 py-1 bg-primary text-white text-xs font-medium rounded-full mb-4">
                      {tier.badge}
                    </span>
                    <h3 className="text-slate-900 font-semibold mb-2">
                      {tier.title}
                    </h3>
                    <p className="text-primary text-sm mb-3">{tier.hardware}</p>
                    <p className="text-slate-600 text-sm">
                      {tier.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            <ScrollReveal>
              <div className="bg-primary/10 rounded-xl p-6 border border-primary/20 text-center">
                <p className="text-slate-900">
                  <strong>Memory Requirements:</strong> Minimum 64GB Unified Memory
                  to support multi-agent systems without performance degradation.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Cognitive Core */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                The Cognitive Core: Agentic Workflows
              </h2>
              <p className="text-slate-600 max-w-3xl mx-auto">
                The Cognitive Core moves beyond rigid logic to "Agentic AI," where
                models perceive environments, plan sequences, and execute actions
                using a Supervisor-Worker pattern.
              </p>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {agentRoles.map((agent, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card text-center h-full">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <agent.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-slate-900 font-semibold mb-2">
                      {agent.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {agent.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            {/* Safety Integration */}
            <ScrollReveal>
              <div className="bg-white rounded-xl p-8 border border-slate-200">
                <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                  Functional Safety Integration
                </h3>
                <p className="text-slate-600 mb-6">
                  The AI system is{' '}
                  <strong className="text-slate-900">
                    strictly separated from functional safety
                  </strong>
                  . This is a fundamental architectural principle.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-primary font-semibold mb-3">AI Can</h4>
                    <ul className="space-y-2">
                      {[
                        'Trigger a "Controlled Stop" (ramp down) for quality reasons',
                        'Optimise parameters within defined bounds',
                        'Alert operators to anomalies',
                        'Suggest corrective actions',
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-slate-600 text-sm"
                        >
                          <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-red-500 font-semibold mb-3">
                      AI Cannot
                    </h4>
                    <ul className="space-y-2">
                      {[
                        'Override Safety PLC logic',
                        'Disable emergency stops (E-Stops)',
                        'Cross "Do Not Cross" safety boundaries',
                        'Execute actions outside hard-coded guardrails',
                      ].map((item, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-slate-600 text-sm"
                        >
                          <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="w-2 h-2 bg-red-500 rounded-full" />
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <p className="text-slate-600 mt-6 text-sm">
                  <strong className="text-slate-900">Principle:</strong> The Safety
                  PLC remains solely responsible for emergency protection. AI
                  commands are restricted by deterministic boundaries that cannot
                  be overridden by probabilistic outputs.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Business Impact */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Business Impact & ROI
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {roiDrivers.map((driver, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-card text-center">
                    <div className="text-4xl sm:text-5xl font-bold gradient-text mb-4">
                      {driver.metric}
                    </div>
                    <h3 className="text-slate-900 font-semibold mb-2">
                      {driver.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {driver.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>

            {/* Phased Rollout */}
            <ScrollReveal>
              <h3 className="text-2xl font-semibold text-slate-900 text-center mb-8">
                Phased Rollout Strategy
              </h3>
            </ScrollReveal>

            <div className="space-y-6">
              {rolloutPhases.map((phase, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="px-3 py-1 bg-primary text-white text-sm font-medium rounded-full">
                        {phase.label}
                      </span>
                      <span className="text-slate-500 text-sm">
                        {phase.duration}
                      </span>
                    </div>
                    <h4 className="text-xl font-semibold text-slate-900 mb-2">
                      {phase.title}
                    </h4>
                    <p className="text-slate-600 mb-3">
                      {phase.description}
                    </p>
                    <div className="bg-primary/10 rounded-lg p-3">
                      <p className="text-primary text-sm">
                        <strong>Outcome:</strong> {phase.outcome}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Why CoDRIVER LABS */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Why CoDRIVER LABS for Manufacturing AI
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {differentiators.map((item, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card h-full">
                    <h3 className="text-slate-900 font-semibold mb-3">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {item.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Ready to Deploy Cognitive Industrial Edge?
              </h2>
              <p className="text-slate-600 mb-8">
                Book a discovery call to discuss how agentic AI can transform your
                manufacturing operations.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center gap-2 group"
                >
                  Book a Discovery Call
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/capabilities"
                  className="btn-secondary"
                >
                  See All Capabilities
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
